import React, { useState, useEffect } from 'react';
import { array, bool, func, object, string } from 'prop-types';

import { connect } from 'react-redux';

import { get, isEmpty, isNil } from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import clsx from 'clsx';

import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';

import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

import EsmsDialog from '../../components/EsmsDialog';
import FileUploadInput from '../../containers/Global/FileUploadInput';
import GoogleLogo from '../../components/GoogleLogo';
import TimeZoneAutoSelect from '../../components/TimeZoneAutoSelect';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as CoreActions from '../../actions/coreActions';
import * as UiActions from '../../actions/uiActions';
import cognitoData from '../../awsConfig';
import classes from './CompanyModal.module.css';

const ValidationSchema = Yup.object().shape({
    name: Yup.string()
        .max(100)
        .required(),
    contractorName: Yup.array()
        .required(),
    addressSearch: Yup.string()
        .required(),
    accentColor: Yup.string()
        .nullable()
        .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, 'Must be formatted as a hex value: #000000'),
    timeZone: Yup.object()
        .required()
});

const EditCompanyModal = ({
    close,
    contractorTypes,
    open,
    title,
    isLoading,
    errors,
    toggleLoading,
    clearErrors,
    ...props
}) => {
    const [address, setAddress] = useState({});
    const [address1, setAddress1] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');
    const [fileImport, setFileImport] = useState(null);
    const [hasCompanyLogo, setHasCompanyLogo] = useState(false);
    const [logoPath, setLogoPath] = useState('');

    useEffect(() => {
        const init =(() => {
            setHasCompanyLogo(props.companyInfo.hasLogo);
            if (props.companyInfo.hasLogo) {
                setLogoPath(`${cognitoData.key2act.applications.companyLogo.url}${props.companyInfo.companyId}/logo.png`);
            }
        });
        if (props.companyInfo) init();
    }, [props.companyInfo]);

    const formik = useFormik({
        initialValues: {
            accentColor:    get(props, 'companyInfo.accentColor', ''),
            addressSearch:  get(props, 'companyInfo.companyAddress', ''),
            contractorName: get(props, 'companyInfo.contractorType', []),
            name:           get(props, 'companyInfo.companyName', ''),
            timeZone:       {
                name:   get(props, 'companyInfo.timeZone', ''),
                offSet: get(props, 'companyInfo.timeZoneOffset', '')
            }
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                updateCompany
            } = props;

            const contractorTypeNames = values.contractorName;
            const companyId = get(props, 'companyInfo.companyId', '');
            const companyInfo = get(props, 'companyInfo', '');
            const hasLogo = get(props,'companyInfo.hasLogo');

            let isUpdated;
            let contractorTypeIds = [];

            contractorTypes.forEach(type => {
                if (contractorTypeNames.includes(type.name)) {
                    contractorTypeIds.push(type.id);
                }
            });

            const body = {
                name:           values.name,
                address,
                contractorTypeIds,
                accentColor:    values.accentColor,
                fileImport,
                timeZone:       values.timeZone.name,
                timeZoneOffset: values.timeZone.offSet,
                scoringEnabled: companyInfo.scoringEnabled
            };

            if (fileImport || hasLogo) body.hasLogo = true;

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isUpdated = await updateCompany(companyId, body);
                }

                if (isUpdated) {
                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleGeocodeAddress = async address => {
        try {
            const results = await geocodeByAddress(address);
            const latLng = await getLatLng(results[0]);

            const streetNumber = results[0].address_components.find(result => {
                return result.types.includes('street_number');
            });

            const route = results[0].address_components.find(result => {
                return result.types.includes('route');
            });

            const city = results[0].address_components.find(result => {
                return result.types.includes('locality');
            });

            const administrativeAreaLevel2 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_2');
            });

            const administrativeAreaLevel3 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_3');
            });

            const state = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_1');
            });

            const zip = results[0].address_components.find(result => {
                return result.types.includes('postal_code');
            });

            const country = results[0].address_components.find(result => {
                return result.types.includes('country');
            });

            const address1Check = streetNumber ? `${streetNumber.long_name} ${route.long_name}` : route.long_name;
            const cityCheck = city ? city.long_name : administrativeAreaLevel3.long_name;

            formik.setFieldValue('addressSearch', results[0].formatted_address);

            setAddress1(address1Check);
            setCity(cityCheck);
            setState(state.short_name);
            setZip(zip.long_name);

            setAddress({
                latitude:                 get(latLng, 'lat', ''),
                longitude:                get(latLng, 'lng', ''),
                administrativeAreaLevel1: get(state, 'short_name', ''),
                administrativeAreaLevel2: get(administrativeAreaLevel2, 'long_name', ''),
                country:                  get(country, 'short_name', ''),
                locality:                 cityCheck,
                postalCode:               get(zip, 'long_name', ''),
                route:                    get(route, 'long_name', ''),
                streetNumber:             streetNumber ? streetNumber.long_name : '',
                formattedAddress:         get(results[0], 'formatted_address', '')
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Error: ', error);
        }
    };

    const handleImport = ({file, fileName}) => {
        setHasCompanyLogo(true);
        setLogoPath(file);
        setFileImport(file);
    };

    const handleImportClear = () => {
        setHasCompanyLogo(props.companyInfo.hasLogo);
        setLogoPath('');
        setFileImport(null);
    };

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        close();
    };

    if (!isEmpty(formik.values.addressSearch) && isEmpty(address1)) {
        handleGeocodeAddress(formik.values.addressSearch);
    }

    if (isNil(props.companyInfo.companyName) || isNil(formik.values.contractorName)) {
        return null;
    }

    return (
        <EsmsDialog
            onClose={handleClose}
            disabled={formik.isSubmitting}
            onConfirm={formik.handleSubmit}
            confirmText="Update"
            open={open}
            isLoading={isLoading}
            title={title}
            cancelable={true}
            errors={errors}
            onRendered={formik.handleReset}
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/companyTreeIcon.svg" alt="new company" />
                        <span>Edit Company</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Enter a unique Company name.
                        </p>
                        <p>
                            Provide a Contractor type(s).
                        </p>
                        <p>
                            Choose the time zone where the Company is located.
                        </p>
                        <p>
                            Upload your Company logo to brand deliverables from BOB.
                        </p>
                        <p>
                            Select your Company accent color, in hex format (#000000) to brand deliverables from BOB.
                        </p>
                        <p>
                            Enter the physical rooftop address into Search for Address; we will fill out the address fields.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Company Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="select-multiple-checkbox"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.contractorName) && Boolean(formik.touched.contractorName)}
                            >
                                Contractor Type
                            </InputLabel>
                            <Select
                                multiple
                                value={formik.values.contractorName}
                                id="contractorName"
                                input={<Input id="select-multiple-checkbox" />}
                                renderValue={selected => selected.join(', ')}
                                onChange={formik.handleChange}
                                name="contractorName"
                                classes={{
                                    root: classes.input
                                }}
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.contractorName) && Boolean(formik.touched.contractorName)}
                            >
                                {contractorTypes.map(contractorType => (
                                    <MenuItem
                                        className={classes.selectMenuItem}
                                        value={contractorType.name}
                                        key={contractorType.id}
                                    >
                                        <Checkbox
                                            color="primary"
                                            checked={formik.values.contractorName.indexOf(contractorType.name) > -1}
                                        />

                                        <span>{contractorType.name}</span>
                                    </MenuItem>
                                ))}
                            </Select>
                            {formik.touched.contractorName &&
                             formik.errors.contractorName && <FormHelperText className={classes.error}>{formik.errors.contractorName}</FormHelperText>}
                        </FormControl>
                        <FormControl className={`${classes.inputWrapper} ${classes.timeZoneWrapper}`}>
                            <TimeZoneAutoSelect
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                value={formik.values.timeZone}
                                error={Boolean(formik.errors.timeZone) && Boolean(formik.touched.timeZone)}
                                helperText={Boolean(formik.touched.timeZone) && formik.errors.timeZone}
                                label="Company Time Zone"
                                placeholder="Company Time Zone"
                            />
                        </FormControl>
                        <FormControl className={classes.fileUploadWrapper}>
                            <InputLabel
                                htmlFor=""
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.contractorName) && Boolean(formik.touched.contractorName)}
                            />
                            <FileUploadInput
                                accept=".png"
                                initialText="Company Logo"
                                upload={handleImport}
                                clear={handleImportClear}
                            />
                        </FormControl>
                        {
                            hasCompanyLogo ? 
                                <div className={classes.companyLogo}>
                                    <img 
                                        // src={`${cognitoData.key2act.applications.companyLogo.url}${props.companyInfo.companyId}/logo.png`} 
                                        src={logoPath}
                                        alt="company logo" 
                                        crossOrigin="anonymous"
                                    />
                                </div>
                                : null
                        }
                        
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="accentColor"
                                name="accentColor"
                                type="text"
                                value={formik.values.accentColor}
                                label="Accent Color"
                                placeholder="#000000"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.accentColor) && Boolean(formik.touched.accentColor)}
                                helperText={Boolean(formik.touched.accentColor) && formik.errors.accentColor}
                            />
                        </FormControl>
                        <PlacesAutocomplete
                            value={formik.values.addressSearch}
                            onChange={address => formik.setFieldValue('addressSearch', address)}
                            onSelect={(handleGeocodeAddress)}
                        >
                            {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                                <React.Fragment>
                                    <FormControl className={classes.inputWrapper}>
                                        <InputLabel
                                            htmlFor="addressSearch"
                                            classes={{
                                                error:   classes.error,
                                                focused: classes.labelFocused,
                                                root:    classes.label
                                            }}
                                            error={Boolean(formik.errors.addressSearch) && Boolean(formik.touched.addressSearch)}
                                        >
                                            Search for Address
                                        </InputLabel>
                                        <Input
                                            {...getInputProps({
                                                name: 'addressSearch',
                                                id:   'addressSearch'
                                            })}
                                            classes={{
                                                error:     classes.cssErrorUnderline,
                                                underline: classes.cssUnderline,
                                                root:      classes.input
                                            }}
                                            error={Boolean(formik.errors.addressSearch) && Boolean(formik.touched.addressSearch)}
                                        />
                                        {formik.touched.addressSearch &&
                                         formik.errors.addressSearch && <FormHelperText className={classes.error}>{formik.errors.addressSearch}</FormHelperText>}
                                    </FormControl>
                                    {suggestions.length > 0 && <Paper className={classes.autoCompleteList}>
                                        <List>
                                            {suggestions.map((suggestion, index) => {
                                                return (
                                                    <div key={index}>
                                                        <ListItem
                                                            button
                                                            className={classes.autoCompleteListItem}
                                                            {...getSuggestionItemProps(suggestion)}
                                                        >
                                                            <ListItemText primary={suggestion.description} />
                                                        </ListItem>
                                                    </div>
                                                );
                                            })}
                                        </List>
                                        <GoogleLogo />
                                    </Paper>}
                                </React.Fragment>
                            )}
                        </PlacesAutocomplete>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="address1"
                                name="address1"
                                type="text"
                                value={address1}
                                label="Address"
                                disabled
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="city"
                                name="city"
                                type="text"
                                value={city}
                                label="City"
                                disabled
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                            />
                        </FormControl>
                        <div className={classes.formRow}>
                            <FormControl className={clsx(classes.inputWrapper, classes.state)}>
                                <TextField
                                    id="state"
                                    name="state"
                                    type="text"
                                    value={state}
                                    label="State"
                                    disabled
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormControl className={clsx(classes.inputWrapper, classes.zip)}>
                                <TextField
                                    id="zip"
                                    name="zip"
                                    type="text"
                                    value={zip}
                                    label="Zip Code"
                                    disabled
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                />
                            </FormControl>
                        </div>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

EditCompanyModal.defaultProps = {
    close:           () => {},
    companyInfo:     {},
    companies:       [],
    contractorTypes: [],
    errors:          [],
    isLoading:       false,
    title:           ''
};

EditCompanyModal.propTypes = {
    close:           func,
    companyInfo:     object,
    companies:       array,
    contractorTypes: array,
    errors:          array,
    isLoading:       bool,
    title:           string
};

const mapStateToProps = state => {
    return {
        companyInfo:     state.companyInfo,
        companies:       state.core.companies,
        contractorTypes: state.companyInfo.contractorTypes,
        errors:          state.messages.errors,
        isLoading:       state.ui.isLoading
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...CoreActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(EditCompanyModal);
